import React from "react";
import * as Mui from "@mui/material";
import { getUploadSignature } from "redux/slice/upload-signature";
import * as env from "shared/env";
import { ClientParameters, CloudinaryEvent, CloudinaryUploadWidget, Source, UploadFile, UploadParameters, WidgetParameters } from "./cloudinary";
import { FeatherIcon } from "component/shared/feather-icon";
import { Upload } from "model/upload";

interface Props {
	callback: (files: Upload[]) => void;
	multiple?: boolean;
	resourceType?: "auto" | "image" | "video" | "raw";
	maxFiles?: number;
	// Overrides the widget's default 600KB cap (MAX-3898 — too small for listing photos). Threaded
	// as a prop rather than raising the default globally, since other callers rely on the 600KB cap.
	maxFileSize?: number;
	// Overrides the client-wide upload preset (env.cloudinaryUploadPreset) for callers that need a
	// dedicated preset (MAX-3898 — listing photos use a "listing-photo" preset).
	uploadPreset?: string;
	// MAX-3930 (D-PM-6): restrict accepted file formats (e.g. ["png","jpeg","jpg"]) and the widget
	// source tabs (e.g. ["local","url"]) — mirrors CF's Cloudinary config (phototourmodify_output_1.cfm:16-23).
	// Omitted by other callers, which keep the widget defaults.
	clientAllowedFormats?: string[];
	sources?: Source[];
	disabled?: boolean;
	label?: string;
	style?: React.CSSProperties;
	icon?: JSX.Element;
	color?: "inherit" | "primary" | "secondary";
	variant?: "text" | "outlined" | "contained";
	endIcon?: JSX.Element;
}

class Component extends React.Component<Props> {
	widget: CloudinaryUploadWidget | null = null;
	uploadParameters: UploadParameters = {
		resourceType: this.props.resourceType,
	};
	clientParameters: ClientParameters = {
		maxFileSize: this.props.maxFileSize || 600000,
		clientAllowedFormats: this.props.clientAllowedFormats,
	}
	limitReachedText = this.props.maxFiles ? {
		"en": {
			"notifications": {
				"limit_reached": `You have reached the maximum of ${this.props.maxFiles} files.`,
			},
		}
	} : undefined
	componentDidMount() {
		const widgetParameters: WidgetParameters = {
			multiple: this.props.multiple || false,
			maxFiles: this.props.maxFiles,
			sources: this.props.sources,
		};
		const uploadPreset = this.props.uploadPreset || (env.live
			? env.cloudinaryUploadPreset || ""
			: "mock-upload-preset");
		getUploadSignature(uploadPreset).then((signedParameters) => {
			const config = {
				cloudName: env.cloudinaryCloudName || "",
				uploadPreset,
				...(signedParameters as object),
				...widgetParameters,
				...this.uploadParameters,
				...this.clientParameters,
				text: this.limitReachedText,
			};
			this.widget = window.cloudinary.createUploadWidget(
				config,
				(error, result) => {
					this.checkUploadResult(error, result);
				}
			);
		});
	}

	checkUploadResult = (error: Error, resultEvent: CloudinaryEvent) => {
		if (error) {
			this.setState({
				openSnackbar: true,
				snackbarMessage: "Upload failed.",
				snackbarSeverity: "error",
			});
		}
		switch(resultEvent.event) {
			case "queues-end": {
				let uploadFiles: UploadFile[] = [];
				if (resultEvent.info.files) {
					uploadFiles = resultEvent.info.files.filter((uploadFile: UploadFile) => uploadFile.status === "success");
					this.props.callback(uploadFiles.map((uploadFile) => {
						return {
							name: uploadFile.name,
							url: uploadFile.uploadInfo.secure_url,
						}
					}));
				}	
				break;
			}
			default: {
				return;
			}
		}
	};

	closeSaveMessage = () => {
		this.setState({
			openSnackbar: false,
		});
	};

	render() {

		const { label, style, icon, color, variant = "contained", endIcon } = this.props;
		return (
			<>
				{icon && !label &&
					<Mui.IconButton 
						id="upload_widget"
						size="small"
						onClick={() => this.widget && this.widget.open()}
						disabled={this.props.disabled}
					>
						<FeatherIcon>
							{icon}
						</FeatherIcon>
					</Mui.IconButton>
				}
				{label &&
					<Mui.Button
						id="upload_widget"
						onClick={() => this.widget && this.widget.open()}
						style={style}
						variant={variant}
						color={color}
						disabled={this.props.disabled}
						startIcon={icon}
						endIcon={endIcon}
					>
						{label}
					</Mui.Button>
				}
			</>
		);
	}
}

export const PhotoUploadDialogButton = Component;
